package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.AstNode
import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DslHeaderAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.ImportDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenKind
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 顶层语法分发：根据首个有效关键字选择 [ScreenParser] 或 [ComponentParser]。
 *
 * screen 文件支持顶层 fun（与 screen 内 fun 共享 forward reference）：
 * 4b660e9 起在 screen 块前后允许出现独立的 `fun NAME(...) { ... }` 声明，
 * 它们被收集到 [ScreenAst.funs] 列表（顶层 fun 在前，screen 内 fun 在后）。
 *
 * Stage 11：新增 `hud { ... }` 顶层块，与 screen 共用同一文件结构（顶层 fun / data 前后置），
 * 仅块关键字差异，由 [ScreenParser] 通过 [ScreenKind] 区分。
 */
internal class TopLevelParser(private val p: Parser) {

    companion object {
        /** `suspend` 是软关键字（词法层为 IDENTIFIER），与 TypeParser / ScreenParser 保持一致。 */
        private const val SOFT_KW_SUSPEND = "suspend"

        /** `import` / `as` 是软关键字（词法层为 IDENTIFIER），用于 ADR-0038 组件别名声明。 */
        private const val SOFT_KW_IMPORT = "import"
        private const val SOFT_KW_AS = "as"
        private const val SOFT_KW_ALLIN = "allin"
        private const val SOFT_KW_DSL = "dsl"
        private const val SOFT_KW_MODE = "mode"
        private const val SOFT_KW_STRICT = "strict"
        private const val SOFT_KW_LEGACY = "legacy"
    }

    fun parse(): AstNode {
        p.skipNewlines()
        val header = parseOptionalHeader()
        if (header != null) p.currentDslMode = header.mode
        p.skipNewlines()
        // ADR-0038：顶层块之前可有 0+ 条 `import "path" as Alias`，screen / component 共用。
        val imports = parseImports()
        p.skipNewlines()
        val node = when (p.current().type) {
            TokenType.KW_SCREEN -> parseScreenOrHudFile(ScreenKind.SCREEN, header)
            TokenType.KW_HUD -> parseScreenOrHudFile(ScreenKind.HUD, header)
            TokenType.KW_COMPONENT -> ComponentParser(p, header).parseComponent()
            TokenType.KW_THEME -> ThemeFileParser(p).parse()
            TokenType.KW_FUN -> parseScreenOrHudFile(ScreenKind.SCREEN, header) // 顶层 fun 出现在 screen / hud 前
            TokenType.KW_DATA -> parseScreenOrHudFile(ScreenKind.SCREEN, header) // M6-a：顶层 data 出现在 screen / hud 前
            TokenType.KW_OBJECT -> parseScreenOrHudFile(ScreenKind.SCREEN, header) // Stage 7a：顶层 object 出现在 screen / hud 前
            TokenType.IDENTIFIER -> {
                // M5-e：顶层 `suspend fun` 前缀等同于 KW_FUN 起始的顶层 fun。
                if (p.current().lexeme == SOFT_KW_SUSPEND && p.peek(1).type == TokenType.KW_FUN) {
                    parseScreenOrHudFile(ScreenKind.SCREEN, header)
                } else {
                    p.fail("顶层期望 screen / hud / component / theme，实际为 '${p.current().lexeme}'")
                }
            }
            else -> p.fail("顶层期望 screen / hud / component / theme，实际为 '${p.current().lexeme}'")
        }
        return attachImports(node, imports)
    }

    /** 解析可选 `@allin(dsl = N, mode = strict|legacy)` 文件头。 */
    private fun parseOptionalHeader(): DslHeaderAst? {
        if (!p.check(TokenType.AT)) return null
        val at = p.advance()
        val name = p.expect(TokenType.IDENTIFIER, "@ 后缺少 allin")
        if (name.lexeme != SOFT_KW_ALLIN) {
            p.fail("未知文件头 @${name.lexeme}", name.position)
        }
        p.expect(TokenType.LPAREN, "@allin 后缺少 '('")
        val values = linkedMapOf<String, String>()
        while (!p.check(TokenType.RPAREN) && !p.check(TokenType.EOF)) {
            val key = p.expect(TokenType.IDENTIFIER, "@allin 参数名缺失")
            p.expect(TokenType.EQUALS, "@allin.${key.lexeme} 后缺少 '='")
            val value = when (key.lexeme) {
                SOFT_KW_DSL -> {
                    val tok = p.expect(TokenType.NUMBER, "dsl 必须为数字")
                    (tok.literalValue as Number).toInt().toString()
                }
                SOFT_KW_MODE -> {
                    val tok = p.expect(TokenType.IDENTIFIER, "mode 必须为 strict 或 legacy")
                    tok.lexeme
                }
                else -> p.fail("@allin 不支持参数 '${key.lexeme}'", key.position)
            }
            values[key.lexeme] = value
            if (p.check(TokenType.COMMA)) {
                p.advance()
            } else if (!p.check(TokenType.RPAREN)) {
                p.fail("@allin 参数之间缺少 ','", p.current().position)
            }
        }
        val close = p.expect(TokenType.RPAREN, "@allin 缺少 ')'")
        val version = values[SOFT_KW_DSL]?.toIntOrNull() ?: 1
        val mode = when (values[SOFT_KW_MODE] ?: SOFT_KW_LEGACY) {
            SOFT_KW_STRICT -> DslMode.STRICT
            SOFT_KW_LEGACY -> DslMode.LEGACY
            else -> p.fail("mode 必须为 strict 或 legacy", at.position)
        }
        if (version != 1 && version != 2) {
            p.fail("dsl 版本仅支持 1 或 2", at.position)
        }
        if (version == 1 && mode != DslMode.LEGACY) {
            p.fail("dsl = 1 仅支持 legacy 模式", at.position)
        }
        if (version == 2 && mode != DslMode.STRICT) {
            p.fail("dsl = 2 仅支持 strict 模式", at.position)
        }
        return DslHeaderAst(version, mode, p.completePosition(at.position, close))
    }

    /** ADR-0038：消费顶层 0+ 条 `import "path" as Alias` 软关键字声明。 */
    private fun parseImports(): List<ImportDecl> {
        val imports = mutableListOf<ImportDecl>()
        while (p.check(TokenType.IDENTIFIER) && p.current().lexeme == SOFT_KW_IMPORT) {
            val importTok = p.advance()
            val pathTok = p.expect(TokenType.STRING, "import 后需跟组件路径字符串")
            val path = pathTok.literalValue as? String
                ?: p.fail("import 路径须为字符串字面量", pathTok.position)
            if (!(p.check(TokenType.IDENTIFIER) && p.current().lexeme == SOFT_KW_AS)) {
                p.fail("import 须为 'import \"path\" as Alias' 形式", p.current().position)
            }
            p.advance() // as
            val aliasTok = p.expect(TokenType.IDENTIFIER, "import 别名须为标识符")
            imports += ImportDecl(path, aliasTok.lexeme, importTok.position)
            p.skipNewlines()
        }
        return imports
    }

    /** 把消费到的 import 列表挂到 screen / component 结果上；theme 不支持 import。 */
    private fun attachImports(node: AstNode, imports: List<ImportDecl>): AstNode {
        if (imports.isEmpty()) return node
        return when (node) {
            is ScreenAst -> node.copy(imports = imports)
            is ComponentAst -> node.copy(imports = imports)
            else -> p.fail("import 仅可用于 screen / component 文件")
        }
    }

    /**
     * 解析 screen 或 hud 文件：可选的前置/后置文件级 fun + 顶层 data / object + 主块。
     *
     * 当文件首 token 为 `fun` / `data` / `object` / `suspend fun` 时，预扫无法立即决定主块种类，
     * 此时默认按 [ScreenKind.SCREEN] 进入；解析到主块时由 ScreenParser 校验关键字。
     * 若用户实际写的是 `hud`，应通过 [TokenType.KW_HUD] 入口直接传入 [ScreenKind.HUD]。
     */
    private fun parseScreenOrHudFile(kind: ScreenKind, header: DslHeaderAst?): AstNode {
        // 4b660e9：先预扫整个 token 流，把主块外的 fun 名也写入 screenFuns，
        // 与主块内 fun 共享 forward reference。
        p.prescanFileLevelFuns()

        val fileFuns = mutableListOf<FunDecl>()
        val classDecls = mutableListOf<ClassDecl>()
        p.skipNewlines()
        // 主块前的顶层 fun / data / object：按出现顺序收集
        consumeFileLevelDecls(fileFuns, classDecls)

        // 主块：根据 kind 选择对应关键字进入 ScreenParser。
        // 实际块种类需要看当前 token：若文件以顶层 fun / data / object 起始进入本方法，
        // 主块可能是 screen 也可能是 hud，按当前 token 重新判定。
        val effectiveKind = when (p.current().type) {
            TokenType.KW_HUD -> ScreenKind.HUD
            TokenType.KW_SCREEN -> ScreenKind.SCREEN
            else -> kind
        }
        val screen = ScreenParser(p, effectiveKind, header).parseScreen()

        p.skipNewlines()
        // 主块后的顶层 fun / data / object：与主块前共用同一收集逻辑
        consumeFileLevelDecls(fileFuns, classDecls)

        val tail = if (effectiveKind == ScreenKind.HUD) {
            "hud 之后仅允许顶层 fun / data / object"
        } else {
            "screen 之后仅允许顶层 fun / data / object"
        }
        p.expect(TokenType.EOF, tail)

        // M6-a：data 同名重复（E765）由 SemanticChecker / DataLiteralChecker 阶段通过
        // diag.error 收集，允许同一编译单元上报多个重复，这里仅保留首个名字到结果列表。
        val withFuns = if (fileFuns.isEmpty()) screen
        else screen.copy(funs = fileFuns + screen.funs)
        return if (classDecls.isEmpty()) withFuns
        else withFuns.copy(classDecls = classDecls)
    }

    /**
     * 主块前后共用的"消费顶层 fun / data / object"循环。
     *
     * 命中 [TokenType.KW_DATA] / [TokenType.KW_OBJECT] → 走 [ClassBlockParser]，
     * 其余按 [isFileFunStart] 视为顶层 fun（含 `suspend fun`）；
     * 每条声明之后吃尽 NEWLINE，便于下一轮判定。
     */
    private fun consumeFileLevelDecls(
        fileFuns: MutableList<FunDecl>,
        classDecls: MutableList<ClassDecl>,
    ) {
        while (
            isFileFunStart() || p.check(TokenType.KW_DATA) || p.check(TokenType.KW_OBJECT)
        ) {
            if (p.check(TokenType.KW_DATA) || p.check(TokenType.KW_OBJECT)) {
                classDecls += ClassBlockParser(p).parse()
            } else {
                fileFuns += parseOneFileFun()
            }
            p.skipNewlines()
        }
    }

    /** 当前位置是否为顶层 fun 起始：`fun ...` 或 `suspend fun ...`。 */
    private fun isFileFunStart(): Boolean {
        if (p.check(TokenType.KW_FUN)) return true
        if (p.check(TokenType.IDENTIFIER) && p.current().lexeme == SOFT_KW_SUSPEND
            && p.peek(1).type == TokenType.KW_FUN) return true
        return false
    }

    /** 解析一个顶层 fun（可能带 `suspend` 前缀）。 */
    private fun parseOneFileFun(): FunDecl {
        val isSuspend = if (p.check(TokenType.IDENTIFIER) && p.current().lexeme == SOFT_KW_SUSPEND) {
            p.advance() // 消费 suspend
            true
        } else {
            false
        }
        return FunDeclParser(p).parseScreenLevel(isSuspend)
    }
}
